import { LineChart } from '@inmarket/inmarkit';
import { Card, CircularProgress, Typography } from '@mui/material';
import React, { useMemo } from 'react';

import useContractCtrData from '../../hooks/useContractCtrData';
import { useContractManagerContext } from '../../hooks/useContractManagerContext';

interface ContractCtrDataProps {
  contractId: number;
}

const lineChartColors = ['#7DBE42', '#0E3461', '#437EEB', '#5F5F63', '#EBA669', '#E06681'];

function ContractCtrData({ contractId }: ContractCtrDataProps) {
  const { data: json, isLoading, isSuccess } = useContractCtrData(contractId);
  const { selectedLineItemIds } = useContractManagerContext();

  const data = json?.data || [];

  const dataByLineItemId = data.reduce((accumulator, { insertion_order_id, x, y }) => {
    if (!accumulator[insertion_order_id]) {
      accumulator[insertion_order_id] = [];
    }
    accumulator[insertion_order_id].push({
      x,
      y,
    });
    return accumulator;
  }, {});

  // selectedLineItemIds is a MUI GridRowSelectionModel (string|number)[], while
  // Object.keys gives us strings. Normalize to a string Set for membership checks.
  const selectedIdSet = useMemo(() => new Set(selectedLineItemIds.map(String)), [selectedLineItemIds]);

  const allLineItemIds = Object.keys(dataByLineItemId);
  const lineItemIds =
    selectedIdSet.size > 0 ? allLineItemIds.filter((id) => selectedIdSet.has(id)) : allLineItemIds;

  const lineItemCtrData = [];
  for (let i = 0; i < lineItemIds.length; i += 1) {
    const lineItemId = lineItemIds[i];

    const seriesData = dataByLineItemId[lineItemId].map((item) => ({
      x: new Date(item.x),
      y: Number(item.y) * 100,
    }));

    const turboThreshold = seriesData.length;
    const color = lineChartColors[Number(lineItemId) % lineChartColors.length];

    lineItemCtrData.push({
      name: `Insertion Order ${lineItemId} CTR`,
      turboThreshold,
      data: seriesData,
      color,
      lineWidth: 0.75,
      tooltip: {
        valueSuffix: '%',
      },
    });
  }

  return (
    <Card sx={{ width: '50%', height: '400px', p: 2 }}>
      {isLoading && <CircularProgress />}
      {isSuccess && lineItemCtrData?.length > 0 && (
        <LineChart title="CTR by Line Items" xAxisType="datetime" data={lineItemCtrData} />
      )}
      {isSuccess && lineItemCtrData?.length === 0 && <Typography>No data</Typography>}
    </Card>
  );
}

export default ContractCtrData;
